import _ from 'lodash';

import { extractEntityIdFromFieldPath, getEntityScopedFieldKey } from 'utils/completeness';
import { jsonStringify } from 'utils/json';

/**
 * Resolves a field's "Needed" entry out of the `selectIncompleteLinkedFields` map.
 *
 * A per-row-entity field's `fieldPath` is entity-scoped (`[<entityId>].leaf`), so an
 * entity-scoped (Borrower/Reo/Income/Asset/Liability) snapshot's item is matched on the
 * entity-scoped key — that is what keeps a sibling row's gap from lighting this row's form
 * (Borrower: DEV-4684; other financial-info rows: DEV-5801).
 *
 * The bare-leaf fallback then catches the item a LoanApplication-target completeness checklist
 * links onto the same row field. Such an item carries no entity, so the selector keys it by
 * bare leaf; without the fallback it lights the nav rail while the field itself stays blank and the
 * operator can neither satisfy nor suppress it (DEV-5145).
 */
export const resolveCompletenessEntry = ({ incompleteLinkedFields, fieldId, fieldPath }) => {
  if (!fieldId) {
    return null;
  }

  const entityId = extractEntityIdFromFieldPath(fieldPath);
  const entityScopedEntry = entityId
    ? incompleteLinkedFields[getEntityScopedFieldKey(fieldId, entityId)]
    : null;

  return entityScopedEntry ?? incompleteLinkedFields[fieldId] ?? null;
};

export const normalizeFormValue = val => {
  if (_.isNil(val)) {
    return null;
  }

  if (_.isObject(val) && !_.isArray(val)) {
    return jsonStringify(val);
  }

  return String(val);
};

export const getInputValue = target => {
  if (_.isNil(target)) {
    return null;
  }

  if (target.type === 'checkbox') {
    return String(target.checked);
  }

  return target.value;
};

export const getCommittedBroadcastDecisionForFormValueChange = (prevValue, nextValue) => {
  const normalizedNext = normalizeFormValue(nextValue);
  const normalizedPrev = normalizeFormValue(prevValue);

  if (_.isNil(normalizedNext) && _.isNil(normalizedPrev)) {
    return { shouldBroadcast: false, value: null };
  }

  if (normalizedNext === normalizedPrev) {
    return { shouldBroadcast: false, value: null };
  }

  return { shouldBroadcast: true, value: normalizedNext };
};

export const wrapperContainsFocus = wrapperEl =>
  !_.isNil(wrapperEl) &&
  !_.isNil(document.activeElement) &&
  wrapperEl.contains(document.activeElement);
